import { FormEvent, useEffect, useState } from 'react';
import { useLoginMutation } from 'app/services';
import type { LoginMfaRedirect } from 'app/services/authApi';
import { useBodyClass } from 'hooks/useBodyClass';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import privacyPolicyTmpl from './policies/privacy_policy.tmpl?raw';
import termsOfUseTmpl from './policies/terms_of_use.tmpl?raw';

// Faithful re-implementation of sign_in.html + mvc/sign_in/view/sign_in.js —
// same markup/classes, styled by the existing common.css. No component library.
//
// Parity with sign_in.js:
//  - #sign starts non-clickable; enabled only when username is non-empty, the
//    password passes the legacy regex, and #agree is checked (checkBtnActivity).
//  - the password field gets .invalid while it fails the regex (isValidPass).
//  - Privacy Policy / Terms of Use open the SAME popup templates the old page
//    used — popups, not redirects.
//  - remember-username cookie (14 days) prefills the username on load.

// Byte-for-byte copies of mvc/sign_in/tmpl/{privacy_policy,terms_of_use}.html.
// They used to be fetched from /static/mvc/... at click time; that directory
// went away with the rest of Backbone, so the fetch 404'd and the links
// silently did nothing. Bundling them with ?raw drops the runtime dependency
// and makes the popups work in every dev mode, not just Django-served ones.
const POLICY_TMPL = {
  privacy_policy: privacyPolicyTmpl,
  terms_of_use: termsOfUseTmpl,
} as const;

type PolicyId = keyof typeof POLICY_TMPL;

// Legacy password rule from sign_in.js isValidPass.
const PASSWORD_REGEX = /^[A-Za-z0-9!@#$%^&*()\-_=+[\]{}|;:'",.<>?/\\]{7,55}$/;

const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  for (let c of document.cookie.split(';')) {
    c = c.trim();
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
};

const setCookie = (name: string, value: string, days?: number) => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`;
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pwTouched, setPwTouched] = useState(false);
  const [agree, setAgree] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [failed, setFailed] = useState(false);
  // Legacy showed the view's own error text (e.g. the 90-day "Please update
  // your password."); null falls back to the generic credentials message.
  const [failText, setFailText] = useState<string | null>(null);
  const [policy, setPolicy] = useState<PolicyId | null>(null);
  const [login, { isLoading }] = useLoginMutation();
  useBodyClass('sign-in');
  useDocumentTitle('Kinetix');

  // Legacy: saved_username cookie prefills the field (the checkbox itself is
  // not pre-checked — matches sign_in.js).
  useEffect(() => {
    const saved = getCookie('username');
    if (saved && saved !== 'null') setUsername(saved);
  }, []);

  const passwordValid = PASSWORD_REGEX.test(password);
  // checkBtnActivity: username non-empty + no empty/invalid form-field + agree.
  const canSubmit = username.trim() !== '' && passwordValid && agree;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Legacy posted the form and navigated away, so a second submit was
    // impossible. Guard in the handler instead — the button itself must not
    // change (see below).
    if (isLoading) return;
    if (!agree) {
      alert('Please indicate that you have read and agree to the Terms of Use and Privacy Policy');
      return;
    }
    // Legacy remember-username: written on submit, cleared when unchecked.
    if (remember) setCookie('username', username, 14);
    else setCookie('username', '');
    setFailed(false);
    setFailText(null);
    try {
      const res = await login({ username, password, remember }).unwrap();
      // IUser carries an index signature, so an `in` check cannot narrow the
      // union; read the discriminant through the redirect shape instead.
      const mfa = res as Partial<LoginMfaRedirect>;
      if (mfa.mfa_required === true) {
        // Legacy: HttpResponseRedirect('/verify-mfa/') — the server-rendered
        // page completes the login from the session keys the API primed.
        window.location.href = mfa.redirect || '/verify-mfa/';
        return;
      }
      window.location.href = '/'; // leave the SPA for the (Backbone) dashboard
    } catch (err) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      setFailText(detail && detail !== 'Invalid username or password.' ? detail : null);
      setFailed(true);
    }
  };

  // Legacy popup-show-btn: show the popup template matching the link id;
  // button[name="ok_btn"] ("I ACCEPT") removes it. Like the legacy handler,
  // clicking the backdrop does NOT close it.
  const onPopupClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).getAttribute('name') === 'ok_btn') setPolicy(null);
  };

  return (
    <div className="sign-in-container">
      <div className="login">
        <div className="login-logo" />
        <div className="form-info">
          <h4 className="form-name">Login</h4>
        </div>
          <form
            id="login-form"
            className={failed ? 'invalid' : ''}
            method="post"
            action="/login/"
            onSubmit={onSubmit}
          >
            <div className="first-input">
              <input
                id="username"
                type="text"
                name="username"
                className="form-field"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <i className="text-clear icon-close-1" onClick={() => setUsername('')} />
            </div>
            <div className="second-input position-relative">
              <input
                id="password"
                type={showPw ? 'text' : 'password'}
                name="password"
                className={`sign-in-pass passowrd-eye-icon form-field${
                  pwTouched && !passwordValid ? ' invalid' : ''
                }`}
                placeholder="Password (case sensitive)"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPwTouched(true);
                }}
              />
              <div className="password-input-btm">
                <div className="password-conditions">
                  <span>Password conditions</span>
                  <ul className="passowrd-tooltip">
                    <li>{`Password characters must be alphanumeric + !@#$%^&*()-_=+[]{}|;:'",.<>?/\\`}</li>
                    <li>Password must be 7 up to 55 characters.</li>
                    <li>Password is case sensitive.</li>
                  </ul>
                </div>
              </div>
              <i
                className={`icon-eye show-hide-password${showPw ? ' active' : ''}`}
                onClick={() => setShowPw((s) => !s)}
              />
            </div>
            <div className="checkbox">
              <ul>
                <li>
                  <input
                    id="remember_username"
                    type="checkbox"
                    name="remember_username"
                    value="1"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  <label htmlFor="remember_username">Remember username</label>
                </li>
                <li>
                  <input
                    id="agree"
                    type="checkbox"
                    name="agreement"
                    value="agree"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                  />
                  <label className="agree">
                    By checking this box, I acknowledge the current{' '}
                    <a
                      id="privacy_policy"
                      className="policy popup-show-btn"
                      onClick={() => setPolicy('privacy_policy')}
                    >
                      {' '}
                      Privacy Policy
                    </a>{' '}
                    and agree to the current{' '}
                    <a
                      id="terms_of_use"
                      className="terms popup-show-btn"
                      onClick={() => setPolicy('terms_of_use')}
                    >
                      Terms of Use.
                    </a>
                  </label>
                </li>
              </ul>
              <div
                className="forgot-password-link"
                style={{ textAlign: 'right', marginTop: 38 }}
              >
                <a href="/forgot-password/">Forgot password?</a>
              </div>
            </div>
            <span className="required-error">{failText ?? 'Invalid username and/or password'}</span>
            <div className="button" style={{ marginTop: -35 }}>
              <input
                id="sign"
                type="submit"
                name="sign"
                // Legacy never relabels or disables #sign while signing in: the
                // label is a static "Sign In" in sign_in.html and sign_in.js
                // only toggles .non-clickable from checkBtnActivity.
                value="Sign In"
                className={canSubmit ? '' : 'non-clickable'}
              />
            </div>
        </form>
      </div>
      {policy && (
        // Legacy $.get(...).append(body) equivalent: static, build-time policy
        // markup — no user input reaches it.
        // eslint-disable-next-line react/no-danger
        <div onClick={onPopupClick} dangerouslySetInnerHTML={{ __html: POLICY_TMPL[policy] }} />
      )}
    </div>
  );
};

export default Login;
